// frontend/src/pages/reports/ExamMarkReportPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, Select, Spin, Typography, Row, Col,
  Statistic, Empty, Tag, message, Tabs, Alert
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAllExams } from '../../services/examService';
import { getAllSubjects } from '../../services/subjectService';
import { getAllClasses } from '../../services/classService';
import { getClassSubjects } from '../../services/assignmentService';
import { getMarksByFilters } from '../../services/markService';
import { useAcademicYear } from '../../context/AcademicYearContext';

const { Title, Text } = Typography;

const ExamMarkReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classSubjectIds, setClassSubjectIds] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examMarks, setExamMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [examsData, subjectsData, classesData] = await Promise.all([
          getAllExams(),
          getAllSubjects(),
          getAllClasses()
        ]);
        setExams(examsData);
        setSubjects(subjectsData);
        setClasses(classesData);
      } catch (error) {
        message.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  useEffect(() => {
    const loadClassSubjects = async () => {
      if (!selectedClass) {
        setClassSubjectIds([]);
        return;
      }
      try {
        const ids = await getClassSubjects(selectedClass);
        setClassSubjectIds(ids);
      } catch (err) {
        console.error('Failed to load class subjects', err);
        setClassSubjectIds([]);
      }
    };
    loadClassSubjects();
  }, [selectedClass]);

  useEffect(() => {
    if (!selectedExam) {
      setExamMarks({});
      return;
    }

    const fetchExamMarks = async () => {
      setLoading(true);
      try {
        // console.log('ExamMarkReportPage - Starting fetch...');
        // console.log('ExamMarkReportPage - Filters:', { exam_id: selectedExam });

        const params = { exam_id: selectedExam };
        if (selectedClass) params.class_id = selectedClass;
        const data = await getMarksByFilters(params);
        // console.log('ExamMarkReportPage - Fetched data:', data);
        // console.log('ExamMarkReportPage - Data length:', data.length);

        // Group by subject_name (API returns flat structure)
        // Optionally limit by class's configured subjects
        const bySubject = {};
        const allowedSubjectIds = classSubjectIds && classSubjectIds.length > 0 ? new Set(classSubjectIds) : null;
        data.forEach(mark => {
          // If class filter is active and there is a configured subject list, skip marks from other subjects
          if (allowedSubjectIds && !allowedSubjectIds.has(mark.subject_id)) return;
          const subject = mark.subject_name || 'Unknown Subject';
          if (!bySubject[subject]) {
            bySubject[subject] = [];
          }
          bySubject[subject].push({
            ...mark,
            key: `${mark.student_id}-${mark.subject_id}-${mark.exam_id}`,
          });
        });

        // console.log('ExamMarkReportPage - Grouped by subject:', Object.keys(bySubject));
        setExamMarks(bySubject);
      } catch (error) {
        message.error('Failed to fetch exam marks');
        console.error('ExamMarkReportPage - Error:', error);
        console.error('ExamMarkReportPage - Error message:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchExamMarks();
  }, [selectedExam, selectedClass, classSubjectIds]);

  const columns = [
    {
      title: 'Admission No',
      dataIndex: 'admission_no',
      key: 'admission_no',
      width: 120,
    },
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
      filterSearch: true,
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score) => score ? parseFloat(score).toFixed(2) : '-',
    },
    {
      title: 'Total Marks',
      dataIndex: 'total_marks',
      key: 'total_marks',
    },
    {
      title: 'Percentage',
      key: 'percentage',
      render: (_, record) => {
        if (!record.score || !record.total_marks) return '-';
        const percent = ((record.score / record.total_marks) * 100).toFixed(2);
        return <Tag color={percent >= 75 ? 'success' : 'orange'}>{percent}%</Tag>;
      },
    },
  ];

  const handleExportExam = () => {
    if (Object.keys(examMarks).length === 0) {
      message.warning('No data to export');
      return;
    }

    const examName = exams.find(e => e.id === selectedExam)?.name || 'Exam';
    const csv = [
      [`Exam Report - ${examName}`],
      [''],
    ];

    Object.entries(examMarks).forEach(([subject, marks]) => {
      csv.push([subject]);
      csv.push(['Admission No', 'Student Name', 'Score', 'Total Marks', 'Percentage']);
      marks.forEach(m => {
        const percent = m.score && m.total_marks ? ((m.score / m.total_marks) * 100).toFixed(2) : '-';
        csv.push([m.admission_no || '-', m.student_name || 'Unknown', m.score || '-', m.total_marks || '-', percent]);
      });
      csv.push(['']);
    });

    const csvString = csv.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exam_marks_${examName}.csv`;
    a.click();
  };

  const tabItems = Object.entries(examMarks).map(([subject, marks]) => ({
    label: subject,
    key: subject,
    children: (
      <Table
        columns={columns}
        dataSource={marks.map((m, i) => ({ ...m, key: i }))}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onShowSizeChange: (current, size) => setPageSize(size),
          onChange: (page, size) => setPageSize(size)
        }}
        scroll={{ x: 600 }}
      />
    ),
  }));

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>📋 Exam-wise Marks Report</Title>
          <Text type="secondary">
            View marks organized and grouped by exam and subject
          </Text>
        </div>

        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <label>Select Exam *</label>
              <Select
                placeholder="Choose an exam"
                value={selectedExam}
                onChange={setSelectedExam}
                loading={loading}
                options={exams.map(e => ({
                  label: e.name,
                  value: e.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <label>Select Class (Optional)</label>
              <Select
                placeholder="Filter by class"
                value={selectedClass}
                onChange={setSelectedClass}
                loading={loading}
                allowClear
                options={classes.map(c => ({
                  label: c.name,
                  value: c.id,
                }))}
                style={{ width: '100%' }}
              />
              {selectedClass && classSubjectIds && classSubjectIds.length === 0 && (
                <div style={{ marginTop: 8 }}>
                  <Alert type="warning" message="No subjects assigned to this class. Configure under Class Subjects (Academic Management)" />
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {selectedExam ? (
          <Card
            title={`${exams.find(e => e.id === selectedExam)?.name || 'Selected Exam'} - Marks by Subject`}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportExam}
                  disabled={Object.keys(examMarks).length === 0}
                >
                  Export CSV
                </Button>
                <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                  Print
                </Button>
              </Space>
            }
          >
            {loading ? (
              <Spin />
            ) : tabItems.length > 0 ? (
              <Tabs items={tabItems} />
            ) : (
              <Empty description="No marks found for this exam" />
            )}
          </Card>
        ) : (
          <Empty description="Please select an exam to view marks" />
        )}
      </Space>
    </div>
  );
};

export default ExamMarkReportPage;
